import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { useFlag } from 'src/hooks/flag';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { useMutation } from 'src/utils/fb-query';
import { authRequest } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../messages';

async function deleteTestCaseRequest(
  repositoryPath: string,
  testCaseUuid: string
): Promise<void> {
  const baseUrl = envBaseUrl('/!api/internal');
  const url = `${baseUrl}/repositories/${repositoryPath}/tests/${testCaseUuid}`;

  const response = await fetch(authRequest(url, { method: 'DELETE' }));

  if (!response.ok) {
    throw new Error(
      `Failed to delete test case: ${response.status} ${response.statusText}`
    );
  }
}

export type UseDeleteTestCaseParams = {
  testName: string;
  onSuccess?: () => void;
  onError?: () => void;
};

export const useDeleteTestCase = ({
  testName,
  onSuccess,
  onError,
}: UseDeleteTestCaseParams) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();
  const repositoryPath = useSelector(getCurrentRepositoryFullSlug) || '';

  const deleteTestCaseCallback = useCallback(
    (testCaseUuid: string) => {
      if (repositoryPath == null || repositoryPath === '') {
        showErrorFlag(
          formatMessage(messages.deleteTestCaseErrorBody),
          formatMessage(messages.deleteTestCaseErrorTitle)
        );
        return Promise.reject(new Error('Repository path is not available'));
      }
      return deleteTestCaseRequest(repositoryPath, testCaseUuid);
    },
    [repositoryPath, showErrorFlag, formatMessage]
  );

  const { mutate: deleteTestCase, isPending: isDeletingTestCase } = useMutation(
    deleteTestCaseCallback,
    {
      onSuccess: () => {
        onSuccess?.();
        showSuccessFlag(
          formatMessage(messages.deleteTestCaseSuccessBody, {
            testName,
          }),
          formatMessage(messages.deleteTestCaseSuccessTitle)
        );
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'pipelines-test-management',
          action: 'delete-test-case',
        });
        showErrorFlag(
          formatMessage(messages.deleteTestCaseErrorBody),
          formatMessage(messages.deleteTestCaseErrorTitle)
        );
        onError?.();
      },
    }
  );

  return {
    deleteTestCase,
    isDeletingTestCase,
  };
};
